<template>
    <v-container class="justify-end mt-0 mt-2">
        <v-row>
            <v-switch
                v-model="wellToggle"
                @update:model-value="toggleSelectMode"
                class="ma-0 pl-4 pt-0"
            ></v-switch>
            <a
                ><b>{{ wellToggle ? "Containers" : "Wells" }}</b></a
            >
        </v-row>
        <v-spacer></v-spacer>
        <v-row v-if="isTubeSeries" class="mr-0 mt-0 mb-1">
            <v-btn @click="selectAllConditions" color="primary" size="x-small">Select All</v-btn>
            <v-btn
                @click="clearConditions"
                color="primary"
                :disabled="selection.length === 0"
                size="x-small"
                class="mx-3"
                >Clear</v-btn
            >
        </v-row>
        <v-row v-else class="mr-0 mt-0 mb-1">
            <v-btn @click="onAssignAllWellsToPlate" color="primary" size="x-small" class="mx-3"
                >Assign All</v-btn
            >
            <v-btn v-if="enableMultiAssign" @click="onAssignMultipleWellsToPlate" color="primary" size="x-small" class="mx-3"
                >Auto-Assign Selected</v-btn
            >
        </v-row>
    </v-container>
    <v-list class="tw-shadow tw-overflow-scroll" style="max-height: 66vh" density="compact">
        <v-list-group
            v-for="condition in activeConditionArray"
            :key="condition.id"
            v-model="expandedMap[condition.id]"
        >
            <template #activator="{ props }">
                <v-list-item v-bind="props" density="compact">
                    <template #prepend>
                        <v-checkbox
                            class="ma-0 py-0"
                            density="compact"
                            :model-value="isParentChecked(condition)"
                            :indeterminate="isParentIndeterminate(condition)"
                            @click.stop
                            @update:model-value="(val) => toggleParentSelection(condition, !!val)"
                            hide-details
                        />
                    </template>
                    <v-list-item-title>{{ condition.name }}</v-list-item-title>
                </v-list-item>
            </template>

            <v-list-item
                v-for="child in condition.children || []"
                :key="child.id"
                density="compact"
            >
                <template #prepend>
                    <v-checkbox
                        class="ma-0 py-0"
                        :model-value="isSelected(child)"
                        @update:model-value="() => toggleSelect(child)"
                        density="compact"
                        hide-details
                    />
                </template>
                <v-list-item-title>{{ child.name }}</v-list-item-title>
                <template #append>
                    <v-badge
                        v-if="(child.count ?? 0) > 0"
                        color="green"
                        :content="child.count ?? 0"
                    ></v-badge>
                </template>
            </v-list-item>
        </v-list-group>
    </v-list>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { useStore } from "vuex";
import { fetchConditionsByExperiment } from "@/services/api.services";
import cloneDeep from "lodash/cloneDeep";

type TreeNode = {
    condition_name?: string;
    id: string | number;
    name: string;
    children?: TreeNode[];
    count?: number;
};

const props = defineProps<{
    experimentId: string;
    autofocus?: boolean;
}>();

const emit = defineEmits<{
    (e: "openOptionsMenu", mode: string): void;
}>();

const store = useStore();
const isTubeSeries = store.getters["plateCreate/isTubeSeries"];
const layout = store.getters["plateCreate/layout"];
const isLoading = ref(true);
const wellToggle = ref(false);
const selection = ref<TreeNode[]>([]);
const activeConditionArray = ref<TreeNode[]>([]);
const wellArray = ref<TreeNode[]>([]);
const containerArray = ref<TreeNode[]>([]);
const expandedConditions = ref<(string | number)[]>([]);
const expandedMap = ref<Record<string | number, boolean>>({});

async function onExperimentId(id: string) {
    try {
        isLoading.value = true;
        const rawConditions = await fetchConditionsByExperiment(id);
        const renamed = (rawConditions as TreeNode[]).map((c: TreeNode) => ({
            ...c,
            name: c.condition_name || c.name,
        }));

        // the wellArray is a list of conditions whose children are wells (remove the containers)
        wellArray.value = renamed.map((condition: TreeNode) => {
            const cloned = cloneDeep(condition);
            const wellChildren = (cloned.children || []).flatMap(
                (container) => container.children || []
            );
            return { ...cloned, children: wellChildren };
        });

        // the containerArray is a list of conditions whose children are containers; the data
        // structure should look exactly like the wellArray, but the children should be containers
        // with their wells removed
        containerArray.value = renamed.map((condition: TreeNode) => {
            const cloned = cloneDeep(condition);
            const containerChildren = (cloned.children || []).map((container) => ({
                ...container,
                children: undefined,
            }));
            return { ...cloned, children: containerChildren };
        });

        return wellToggle.value ? containerArray.value : wellArray.value;
    } finally {
        isLoading.value = false;
    }
}

function toggleSelectMode() {
    activeConditionArray.value = wellToggle.value ? containerArray.value : wellArray.value;
}

function isSelected(node: TreeNode) {
    return selection.value.some((n) => n.id === node.id);
}

function toggleSelect(node: TreeNode) {
    const exists = isSelected(node);
    selection.value = exists
        ? selection.value.filter((n) => n.id !== node.id)
        : [...selection.value, node];

    emitSelection();
}

function toggleParentSelection(parent: TreeNode, value: boolean) {
    const children = parent.children || [];
    if (value) {
        const newItems = children.filter((child) => !isSelected(child));
        selection.value = [...selection.value, ...newItems];
    } else {
        const childIds = children.map((c) => c.id);
        selection.value = selection.value.filter((n) => !childIds.includes(n.id));
    }

    emitSelection();
}

function isParentChecked(parent: TreeNode) {
    const children = parent.children || [];
    return children.length > 0 && children.every((child) => isSelected(child));
}

function isParentIndeterminate(parent: TreeNode) {
    const children = parent.children || [];
    const selectedCount = children.filter((child) => isSelected(child)).length;
    return selectedCount > 0 && selectedCount < children.length;
}

function emitSelection() {
    const leafNodes = selection.value.filter((n) => !n.children);
    store.dispatch("plateCreate/setSampleConditionSelection", leafNodes);
    updateCounts();
}

function selectAllConditions() {
    const childNodes = activeConditionArray.value.flatMap((c) => c.children || []);
    selection.value = [...childNodes];
    emitSelection();
}

function clearConditions() {
    selection.value = [];
    emitSelection();
}

function updateCounts() {
    const countMap: Record<string, number> = {};
    for (const row of layout) {
        for (const cell of row) {
            if (cell.condition) {
                const id = cell.condition.id;
                countMap[id] = (countMap[id] || 0) + 1;
            }
        }
    }
    applyCounts(activeConditionArray.value, countMap);
}

function applyCounts(items: TreeNode[], usageMap: Record<string, number>) {
    for (const item of items) {
        item.count = usageMap[item.id as string] || 0;
        if (item.children) {
            applyCounts(item.children, usageMap);
        }
    }
}

function onAssignAllWellsToPlate() {
    selection.value = wellArray.value;
    // get only the wells from the wellArray
    const childWells = wellArray.value.flatMap((c) => c.children || []);
    store.dispatch("plateCreate/setSampleConditionSelection", childWells);
    emit("openOptionsMenu", "assignAll");
}

function onAssignMultipleWellsToPlate() {
    const childWells = selectedLeafNodes.value;
    store.dispatch("plateCreate/setSampleConditionSelection", childWells);
    emit("openOptionsMenu", "assignMultiple");
}

const selectedLeafNodes = computed(() => {
    return selection.value.filter((n) => !n.children);
})

const enableMultiAssign = computed(() => {
    return selectedLeafNodes.value.length > 0 && !isTubeSeries.value;
});

watch(
    () => props.experimentId,
    async (newId) => {
        const result = await onExperimentId(newId);
        activeConditionArray.value = result;

        // Expand all by default
        expandedMap.value = {};
        for (const item of result) {
            expandedMap.value[item.id] = true;
        }
    },
    { immediate: true }
);

function onActivePlateChange() {
    expandedConditions.value = [];
    updateCounts();
}

onMounted(() => {
    document.addEventListener("activePlateChange", onActivePlateChange);
});

onUnmounted(() => {
    document.removeEventListener("activePlateChange", onActivePlateChange);
});
</script>

<style scoped></style>
